import { CssBaseline, PaletteMode } from '@mui/material'
import { ThemeProvider } from '@mui/system'
import { FC, useMemo, useState } from 'react'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { AppUpdater, FeatureFlagProvider, EventChainProvider } from './components/common'
import { ScreenReaderAnnouncer } from './components/common/ScreenReaderAnnouncer'
import { Home, Test, CustomLogin, Error } from './screens'
import { ROUTES } from './constant'
import { getTheme } from './theme'
import { AuthGuard, ImpersonationBannerContainer } from './components'
import { SnackbarProvider } from 'notistack'
import { HighlightGlobalStyles } from './styles'
import { getTerm } from './helpers'

const brandName = getTerm('MasteryPrep')

interface AppProps {}

const App: FC<AppProps> = () => {
  const [mode] = useState<PaletteMode>('light')

  const theme = useMemo(() => getTheme(mode), [mode])

  return (
    <ThemeProvider theme={theme}>
      <HighlightGlobalStyles />

      <SnackbarProvider
        maxSnack={3}
        autoHideDuration={3000}
        anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
      >
        <FeatureFlagProvider>
          <EventChainProvider>
            <Helmet>
              <title>{brandName}</title>
            </Helmet>

            <ScreenReaderAnnouncer />

            <AppUpdater />

            <ImpersonationBannerContainer />

            <CssBaseline />

            <BrowserRouter>
              <Routes>
                <Route path={ROUTES.home} element={<Home />} />
                <Route
                  path={ROUTES.test}
                  element={
                    <AuthGuard>
                      <Test />
                    </AuthGuard>
                  }
                />
                <Route path={ROUTES.customLogin} element={<CustomLogin />} />
                <Route path={ROUTES.permissionDenied} element={<Error code={403} />} />
                <Route path={ROUTES.validationFailed} element={<Error code={401} />} />
                <Route path={ROUTES.error500} element={<Error code={500} />} />
                <Route path={ROUTES.anyPage} element={<Error code={404} />} />
              </Routes>
            </BrowserRouter>
          </EventChainProvider>
        </FeatureFlagProvider>
      </SnackbarProvider>
    </ThemeProvider>
  )
}

export default App
